import { getTokensFromCache } from '../auth/utils/tokenCache';

export interface ConversationSummary {
  conversation_id: string;
  preview: string;
  updated_at?: number;
}

export interface ConversationMessageRecord {
  message_id?: number | string;
  created_at?: number;
  text?: string;
  actor?: string;
}

export interface ConversationDetail {
  conversation_id: string;
  messages: ConversationMessageRecord[];
  count?: number;
}

const getConversationsApiRoot = (): string | null => {
  const configuredEndpoint = process.env.REACT_APP_APIGATEWAY_CONVERSATIONS_ENDPOINT;
  if (!configuredEndpoint) return null;

  return configuredEndpoint
    .replace(/\/conversations\/?$/, '')
    .replace(/\/conversation\/?$/, '')
    .replace(/\/$/, '');
};

const getAuthHeaders = () => {
  const tokens = getTokensFromCache();
  if (!tokens?.idToken) {
    throw new Error('User not authenticated');
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${tokens.idToken}`,
  };
};

const requestConversationsApi = async <T>(path: string): Promise<T> => {
  const apiRoot = getConversationsApiRoot();
  if (!apiRoot) {
    throw new Error('Conversations API is not configured');
  }

  const response = await fetch(`${apiRoot}${path}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch conversations: ${response.statusText}`);
  }

  return response.json() as Promise<T>;
};

const getPreviewText = (item: Record<string, unknown>): string => {
  const preview = item.preview || item.last_message || item.summary || item.title || item.subject;
  if (typeof preview === 'string' && preview.trim()) {
    return preview.trim();
  }

  const messages = item.messages;
  if (Array.isArray(messages) && messages.length > 0) {
    const firstUserMessage = messages.find((message) => message?.actor === 'user');
    const candidate = firstUserMessage?.text || messages[0]?.text;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return 'Previous chat';
};

const getUpdatedAt = (item: Record<string, unknown>): number | undefined => {
  const timestamp = item.updated_at || item.last_message_at || item.created_at || item.timestamp;
  return typeof timestamp === 'number' ? timestamp : undefined;
};

export const normalizeConversationSummaries = (data: unknown): ConversationSummary[] => {
  const payload = (data || {}) as Record<string, unknown>;
  const items = Array.isArray(data)
    ? data
    : (payload.conversations || payload.items || payload.results || []);

  if (!Array.isArray(items)) return [];

  return items
    .map((item): ConversationSummary | null => {
      const record = (item || {}) as Record<string, unknown>;
      const conversationId = record.conversation_id || record.conversationId || record.id;

      if (!conversationId || typeof conversationId !== 'string') {
        return null;
      }

      return {
        conversation_id: conversationId,
        preview: getPreviewText(record),
        updated_at: getUpdatedAt(record) ?? 0,
      };
    })
    .filter((item): item is ConversationSummary => item !== null)
    .sort((left, right) => (right.updated_at || 0) - (left.updated_at || 0));
};

export const listConversations = async (): Promise<ConversationSummary[]> => {
  const data = await requestConversationsApi<unknown>('/conversations');
  return normalizeConversationSummaries(data);
};

export const getConversation = async (conversationId: string): Promise<ConversationDetail> => {
  const data = await requestConversationsApi<ConversationDetail>(
    `/conversation?conversation_id=${encodeURIComponent(conversationId)}`,
  );

  return {
    conversation_id: data.conversation_id || conversationId,
    messages: data.messages || [],
    count: data.count,
  };
};

export const formatConversationTimestamp = (timestamp?: number): string => {
  if (!timestamp) return '';

  const date = new Date(timestamp > 1_000_000_000_000 ? timestamp : timestamp * 1000);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

export const truncateConversationPreview = (preview: string, maxLength = 42): string => {
  const trimmed = preview.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1)}…`;
};
